import { Box } from '@mui/material';
import { KitButton } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitAccordion,
    KitField,
    KitFormBar,
    KitGrid2,
    KitInput,
    KitSelect,
    KitSwitch,
    KitTextarea,
} from '../common/MobileKitExtras';
import FkField, { FkInlineLink } from '../common/MobileFormFields';
import { useState } from 'react';

/**
 * Formulário de serviço no shell mobile — `prototipos-mobile/servicos/form.html`.
 * Só apresentação: valores, validação (Yup/Formik), categorias, tarefas e envio vêm de
 * `pages/services/Form.js`.
 */

const ChecksIcon = protoIcon('checks');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

const RECURRENCE_UNITS = [
    { id: 'DAY', name: 'Dia(s)' },
    { id: 'WEEK', name: 'Semana(s)' },
    { id: 'MONTH', name: 'Mês(es)' },
    { id: 'YEAR', name: 'Ano(s)' },
];

// Extra do app real (sem equivalente no protótipo): checklist de tarefas do serviço (só na edição).
function TasksBlock({ tasks }) {
    const [open, setOpen] = useState(false);
    return (
        <KitAccordion
            open={open}
            icon={ChecksIcon}
            title="Tarefas do serviço"
            subtitle="Checklist de etapas executadas neste serviço"
            onToggle={() => setOpen((v) => !v)}
        >
            <KitField label="Nome da tarefa" required>
                <KitInput type="text" value={tasks.form.task_name} onChange={(e) => tasks.onFormChange({ ...tasks.form, task_name: e.target.value })} />
            </KitField>
            <KitField label="Ordem">
                <KitInput type="text" inputMode="numeric" value={tasks.form.sequence_order} onChange={(e) => tasks.onFormChange({ ...tasks.form, sequence_order: parseInt(e.target.value, 10) || 0 })} />
            </KitField>
            <KitField label="Descrição da tarefa">
                <KitTextarea value={tasks.form.task_description} onChange={(e) => tasks.onFormChange({ ...tasks.form, task_description: e.target.value })} />
            </KitField>
            <Box sx={{ display: 'flex', gap: '10px', mb: '14px' }}>
                {tasks.editing && <KitButton sx={{ flex: 1 }} onClick={tasks.onCancelEdit}>Cancelar</KitButton>}
                <KitButton variant="soft" sx={{ flex: 1 }} onClick={tasks.editing ? tasks.onUpdate : tasks.onAdd} disabled={tasks.loading}>
                    {tasks.editing ? 'Atualizar tarefa' : 'Adicionar tarefa'}
                </KitButton>
            </Box>
            {tasks.items.length === 0 && !tasks.loading && (
                <Box sx={{ fontSize: 12, color: 'text.disabled', m: '0 2px 14px', lineHeight: 1.4 }}>Nenhuma tarefa cadastrada. Adicione tarefas para este serviço.</Box>
            )}
            {tasks.items.map((task) => (
                <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 0', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4 }}>{task.task_name}</Box>
                        <Box sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                            Ordem {task.sequence_order}{task.task_description ? ` · ${task.task_description}` : ''}
                        </Box>
                    </Box>
                    <Box component="button" type="button" aria-label="Editar tarefa" onClick={() => tasks.onEdit(task)} sx={taskButtonSx}>
                        <EditIcon size={18} />
                    </Box>
                    <Box component="button" type="button" aria-label="Excluir tarefa" onClick={() => tasks.onDelete(task.id)} sx={{ ...taskButtonSx, color: 'error.main' }}>
                        <TrashIcon size={18} />
                    </Box>
                </Box>
            ))}
        </KitAccordion>
    );
}

const taskButtonSx = {
    width: 44,
    height: 44,
    borderRadius: '12px',
    border: 0,
    background: 'none',
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary',
    cursor: 'pointer',
    flexShrink: 0,
};

export default function MobileServiceForm({
    formik,
    isEditing,
    usesVehicle,
    categories,
    subcategories,
    loadingSubcategories,
    onChangeCategory,
    onChangeSubcategory,
    onNewCategory,
    onNewSubcategory,
    onChangePrice,
    onChangeExternalPrice,
    onToggleGeneric,
    onToggleExternal,
    tasks,
    onCancel,
    loading,
}) {
    const { values } = formik;
    const descriptionError = (formik.touched.description && formik.errors.description) || '';

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={formik.handleSubmit} noValidate>
            <FkField formik={formik} name="name" label="Nome do serviço" required type="text" placeholder="Ex.: Alinhamento" />

            <FkField
                formik={formik}
                name="price"
                label="Preço (R$)"
                required
                type="text"
                inputMode="decimal"
                onChange={onChangePrice}
            />

            <KitField label="Categoria">
                <KitSelect value={values.category_id} onChange={(e) => onChangeCategory(e.target.value)} aria-label="Categoria">
                    <option value="">Selecione</option>
                    {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </KitSelect>
                <FkInlineLink label="Nova categoria" onClick={onNewCategory} />
            </KitField>

            <KitField label="Subcategoria" help={!values.category_id ? 'Selecione uma categoria primeiro' : undefined}>
                <KitSelect
                    value={values.subcategory_id}
                    onChange={(e) => onChangeSubcategory(e.target.value)}
                    disabled={!values.category_id || loadingSubcategories}
                    aria-label="Subcategoria"
                >
                    <option value="">Selecione</option>
                    {subcategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </KitSelect>
                <FkInlineLink label="Nova subcategoria" onClick={onNewSubcategory} disabled={!values.category_id} />
            </KitField>

            <KitField label="Descrição" help={descriptionError || `${(values.description || '').length}/100`} error={Boolean(descriptionError)}>
                <KitTextarea
                    name="description"
                    value={values.description}
                    onChange={formik.handleChange}
                    maxLength={100}
                    sx={descriptionError ? { borderColor: 'error.main' } : undefined}
                />
            </KitField>

            {usesVehicle && (
                <>
                    <KitSwitch
                        title="Recorrente"
                        description="Sugere o próximo serviço por KM ou data"
                        checked={values.is_recurring}
                        onChange={(e) => formik.setFieldValue('is_recurring', e.target.checked)}
                    />
                    {values.is_recurring && (
                        <>
                            <KitGrid2>
                                <FkField formik={formik} name="recurrence_km" label="A cada (KM)" type="text" inputMode="numeric" />
                                <FkField formik={formik} name="recurrence_interval" label="A cada" required type="text" inputMode="numeric" />
                            </KitGrid2>
                            <KitGrid2>
                                <FkField formik={formik} name="recurrence_interval_unit" label="Unidade" as="select">
                                    {RECURRENCE_UNITS.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                                </FkField>
                                <FkField formik={formik} name="recurrence_reminder_days" label="Lembrete (dias)" required type="text" inputMode="numeric" />
                            </KitGrid2>
                        </>
                    )}
                </>
            )}

            <KitSwitch
                title="Serviço genérico"
                description={values.is_generic ? 'Sem valor fixo — definido na hora da venda' : 'Ative para serviços sem um valor fixo padrão'}
                checked={values.is_generic}
                onChange={(e) => onToggleGeneric(e.target.checked)}
            />
            <KitSwitch
                title="Serviço externo"
                description={values.is_external ? 'Executado por empresa externa; informe o custo pago a ela.' : 'Ative quando o serviço é terceirizado a outra empresa'}
                checked={values.is_external}
                onChange={(e) => onToggleExternal(e.target.checked)}
            />
            {values.is_external && (
                <FkField
                    formik={formik}
                    name="external_price"
                    label="Custo externo (R$)"
                    type="text"
                    inputMode="decimal"
                    onChange={onChangeExternalPrice}
                    help="Valor pago à empresa que executou o serviço."
                />
            )}

            {isEditing && tasks && <TasksBlock tasks={tasks} />}

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={formik.handleSubmit} disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
